import {useCallback, useEffect, useRef, useState} from 'react';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import {AutosaveValues, AutosaveResult, AutosaveStatus} from './types';

interface PendingField {
    value: unknown;
    version: number;
    ready: boolean;
}

/** Internal scheduling contract; the rc-form adapter handles controls and validation. */
export interface SaveQueueOptions {
    recordKey: string;
    initialValues: AutosaveValues;
    delay?: number;
    isEqual?: (key: string, left: unknown, right: unknown) => boolean;
    disabled: boolean;
    isCollecting?: (key: string) => boolean;
    validate: (keys: string[]) => Promise<{values: AutosaveValues; invalidKeys: string[]}>;
    save: (values: AutosaveValues) => Promise<AutosaveResult | void>;
    reset: (keys: string[]) => void;
    onBusyChange?: (busy: boolean) => void;
}

/**
 * Debounces typing per field and serializes validated changed-field saves.
 * A dropdown never flushes another field's typing timer. Versioned pending entries
 * protect edits made during validation or persistence. Failures retain the draft
 * for explicit retry; successful saves drain any newer eligible edits.
 */
export function useSaveQueue(options: SaveQueueOptions) {
    const latest = useRef(options);
    latest.current = options;
    const baseline = useRef({...options.initialValues});
    const pending = useRef(new Map<string, PendingField>());
    const inFlight = useRef(new Map<string, PendingField>());
    const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
    const drainTimer = useRef<ReturnType<typeof setTimeout>>();
    const running = useRef(false);
    const mounted = useRef(true);
    const version = useRef(0);
    const generation = useRef(0);
    const recordKey = useRef(options.recordKey);
    const previousInitial = useRef<AutosaveValues>({});
    const [status, setStatus] = useState<AutosaveStatus>({
        phase: 'idle',
        busy: false,
    });

    const statusRef = useRef(status);
    statusRef.current = status;
    const drainRef = useRef<() => Promise<void>>();

    function report(phase: AutosaveStatus['phase'], error?: string) {
        if (mounted.current) {
            setStatus({phase, error, busy: running.current || pending.current.size > 0});
        }
    }

    /** Defers validation until rc-form has collected the control's change event. */
    const scheduleDrain = useCallback(() => {
        clearTimeout(drainTimer.current);
        drainTimer.current = setTimeout(() => {
            void drainRef.current?.();
        }, 0);
    }, []);

    /** Validates eligible field versions and applies one request before draining newer edits. */
    async function drain() {
        if (running.current || latest.current.disabled || !mounted.current) {
            return;
        }
        const eligible = Array.from(pending.current.entries()).filter(([, field]) => field.ready);
        if (!eligible.length) {
            return;
        }
        const requestGeneration = generation.current;
        const callbacks = latest.current;
        running.current = true;
        report('saving');
        let succeeded = false;
        try {
            const validation = await callbacks.validate(eligible.map(([key]) => key));
            if (!mounted.current || requestGeneration !== generation.current) {
                return;
            }
            const sent = eligible.filter(([key, field]) => {
                if (validation.invalidKeys.includes(key)) {
                    const current = pending.current.get(key);
                    if (current?.version === field.version) {
                        current.ready = false;
                    }
                    return false;
                }
                if (pending.current.get(key)?.version !== field.version) {
                    return false;
                }
                // A reset/hydration after collection is not a new user edit to persist.
                if (!equal(key, validation.values[key], field.value)) {
                    pending.current.delete(key);
                    return false;
                }
                return true;
            });
            if (latest.current.disabled) {
                return;
            }
            if (!sent.length) {
                succeeded = true;
                report(
                    validation.invalidKeys.length
                        ? 'invalid'
                        : pending.current.size
                        ? 'waiting'
                        : 'idle'
                );
                return;
            }

            const values: AutosaveValues = {};
            sent.forEach(([key]) => {
                values[key] = cloneDeep(validation.values[key]);
            });
            inFlight.current = new Map(sent);
            const result: AutosaveResult = (await callbacks.save(values)) || {};
            if (!mounted.current || requestGeneration !== generation.current) {
                return;
            }
            const resets: string[] = [];
            sent.forEach(([key, field]) => {
                const cancelled = result?.cancelledKeys?.includes(key);
                if (!cancelled) {
                    baseline.current[key] = cloneDeep(values[key]);
                }
                if (pending.current.get(key)?.version === field.version) {
                    pending.current.delete(key);
                    if (cancelled) {
                        resets.push(key);
                    }
                }
            });
            if (resets.length) {
                latest.current.reset(resets);
            }
            succeeded = true;
            report(
                pending.current.size
                    ? 'waiting'
                    : result?.cancelledKeys?.length === sent.length
                    ? 'idle'
                    : 'saved'
            );
        } catch (error) {
            if (!mounted.current || requestGeneration !== generation.current) {
                return;
            }
            report('error', error instanceof Error ? error.message : 'Unable to save changes.');
        } finally {
            if (requestGeneration === generation.current) {
                running.current = false;
                inFlight.current.clear();
                if (mounted.current) {
                    setStatus((current) => ({...current, busy: pending.current.size > 0}));
                    if (
                        succeeded &&
                        Array.from(pending.current.values()).some((field) => field.ready)
                    ) {
                        scheduleDrain();
                    }
                }
            }
        }
    }

    drainRef.current = drain;

    /** Schedules one control's edit without advancing another control's typing timer. */
    function change(key: string, value: unknown, mode: 'typing' | 'immediate') {
        clearTimeout(timers.current.get(key));
        timers.current.delete(key);
        const acknowledged = inFlight.current.has(key)
            ? inFlight.current.get(key)?.value
            : baseline.current[key];
        if (equal(key, value, acknowledged)) {
            pending.current.delete(key);
            report(running.current ? 'saving' : pending.current.size ? 'waiting' : 'idle');
            return;
        }
        pending.current.set(key, {
            value: cloneDeep(value),
            version: ++version.current,
            ready: mode === 'immediate',
        });
        report(running.current ? 'saving' : 'waiting');
        if (mode === 'typing') {
            timers.current.set(
                key,
                setTimeout(() => {
                    timers.current.delete(key);
                    const field = pending.current.get(key);
                    if (field) {
                        field.ready = true;
                    }
                    scheduleDrain();
                }, latest.current.delay ?? 700)
            );
        } else {
            scheduleDrain();
        }
    }

    /** Holds an unfinished composition without discarding its previous pending value. */
    function pause(key: string) {
        clearTimeout(timers.current.get(key));
        timers.current.delete(key);
        const field = pending.current.get(key);
        if (field) {
            field.ready = false;
        }
    }

    /** Makes a field eligible on blur/Enter, or all pending fields on an explicit Retry. */
    function flush(key?: string) {
        const keys = key ? [key] : Array.from(pending.current.keys());
        keys.forEach((fieldKey) => {
            clearTimeout(timers.current.get(fieldKey));
            timers.current.delete(fieldKey);
            const field = pending.current.get(fieldKey);
            if (field) {
                field.ready = true;
            }
        });
        scheduleDrain();
    }

    /** Seeds a field during decoration without duplicating its initialValue in hook configuration. */
    function register(key: string, value: unknown) {
        if (!(key in baseline.current)) {
            baseline.current[key] = cloneDeep(value);
        }
    }

    /** Optional feature comparison handles domain values without putting field names in this queue. */
    function equal(key: string, left: unknown, right: unknown) {
        return latest.current.isEqual
            ? latest.current.isEqual(key, left, right)
            : isEqual(left, right);
    }

    // Record changes invalidate queued validation and late responses from the previous record.
    useEffect(() => {
        if (recordKey.current === options.recordKey) {
            return;
        }

        generation.current += 1;
        recordKey.current = options.recordKey;
        clearTimeout(drainTimer.current);
        timers.current.forEach((timer) => clearTimeout(timer));
        timers.current.clear();
        pending.current.clear();
        inFlight.current.clear();
        running.current = false;
        baseline.current = cloneDeep(options.initialValues);
        previousInitial.current = cloneDeep(options.initialValues);
        latest.current.reset(Object.keys(options.initialValues));
        report('idle');
    }, [options.recordKey, options.initialValues]);

    // Compare incoming defaults with previous defaults, not the last write. An unchanged,
    // stale parent render after a save must not reset the form back to its old initialValue.
    useEffect(() => {
        const resetKeys: string[] = [];
        for (const key of Object.keys(options.initialValues)) {
            const changed =
                key in previousInitial.current &&
                !equal(key, previousInitial.current[key], options.initialValues[key]);
            if (
                changed &&
                !pending.current.has(key) &&
                !inFlight.current.has(key) &&
                !latest.current.isCollecting?.(key)
            ) {
                baseline.current[key] = cloneDeep(options.initialValues[key]);
                resetKeys.push(key);
            }
        }
        previousInitial.current = cloneDeep(options.initialValues);
        if (resetKeys.length) {
            latest.current.reset(resetKeys);
        }
    }, [options.initialValues]);

    useEffect(() => {
        latest.current.onBusyChange?.(status.busy);
    }, [status.busy]);
    useEffect(() => {
        if (!options.disabled && statusRef.current.phase !== 'error') {
            scheduleDrain();
        }
    }, [options.disabled, scheduleDrain]);
    useEffect(
        () => () => {
            mounted.current = false;
            clearTimeout(drainTimer.current);
            timers.current.forEach((timer) => clearTimeout(timer));
            latest.current.onBusyChange?.(false);
        },
        []
    );

    return {status, register, change, pause, flush, retry: () => flush()};
}
